import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  Tent, MapPin, Calendar, Users, Clock, Loader2, ChevronDown, ChevronUp, Eye, Mail, Phone, HelpCircle,
  AlertTriangle, Stethoscope, Sunrise, Camera, CameraOff, Download
} from 'lucide-react';
import { toast } from 'sonner';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// Format an ISO YYYY-MM-DD into "Mon, Jun 8, 2026" — built locally so the
// modal/PDF aren't pulling in a full date library for one helper.
const formatSessionDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
};

export default function AdminCampsScheduleView() {
  const { api } = useAuth();
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [rosterDialog, setRosterDialog] = useState({
    open: false,
    course: '',
    location: '',
    dayNumber: null,
    sectionId: null,
    weekNum: null,
    sessionDate: null,   // ISO YYYY-MM-DD of the specific day clicked
    enrollments: [],
    loading: false,
  });
  // Bug 2/3/4 — RSS_Emergent_Fixes_May_11:
  // Per-camp enrollment-stats response keyed by camp_id. Drives:
  //   • header AVG + FCR side-by-side (Bug 4)
  //   • per-cell X/Y inside the day × location matrix (Bug 3)
  //   • accurate enrollment_count in the location pill above the table (Bug 2)
  const [campStats, setCampStats] = useState({});

  const fetchCamps = useCallback(async () => {
    try {
      const response = await api.get('/camps/?active_only=false&page_size=100');
      const data = response.data.results || response.data;
      const detailed = await Promise.all(data.map(c => api.get(`/camps/${c.id}/?active_only=false`).then(r => r.data)));
      setCamps(detailed);
      // Fetch per-camp enrollment-stats in parallel. Promise.allSettled so a
      // single failed stats call (rare — admin role required) doesn't blank
      // the whole matrix.
      const statsResults = await Promise.allSettled(
        detailed.map(c => api.get(`/camps/${c.id}/enrollment-stats/`).then(r => [c.id, r.data]))
      );
      const statsMap = {};
      statsResults.forEach(r => {
        if (r.status === 'fulfilled' && r.value) {
          const [campId, payload] = r.value;
          statsMap[campId] = payload;
        }
      });
      setCampStats(statsMap);
      // Auto-expand first week
      if (detailed.length > 0) {
        setExpandedWeeks({ [detailed[0].id]: true });
      }
    } catch {
      toast.error('Failed to load camps');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchCamps(); }, [fetchCamps]);

  const toggleWeek = (campId) => {
    setExpandedWeeks(prev => ({ ...prev, [campId]: !prev[campId] }));
  };

  const fetchRoster = async ({ courseTitle, locationName, sectionId, dayNumber, weekNum, sectionStartDate }) => {
    // Derive the actual session date (camp_section.start_date + dayNumber-1).
    // sectionStartDate arrives as ISO 'YYYY-MM-DD'; build a Date from its
    // parts so we don't trip on local-timezone shifts.
    let sessionDate = null;
    if (sectionStartDate && Number.isFinite(dayNumber)) {
      const [y, m, d] = String(sectionStartDate).split('-').map(Number);
      if (y && m && d) {
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + (dayNumber - 1));
        // Format back to ISO YYYY-MM-DD so consumers can format freely.
        const pad = (n) => String(n).padStart(2, '0');
        sessionDate = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
      }
    }
    setRosterDialog({
      open: true,
      course: courseTitle,
      location: locationName,
      dayNumber,
      sectionId,
      weekNum,
      sessionDate,
      enrollments: [],
      loading: true,
    });
    try {
      // Bug fix (Feb 2026): the previous filter compared `e.section_title`
      // to the day's course title — but for camp enrollments,
      // `section_title` is the CAMP name (e.g. "STEM Week 1: Jun 8-12 —
      // Grades 3-8"), not the per-day course title. The match never hit,
      // so the modal always reported "No students enrolled yet" even
      // when cells showed live counts.
      //
      // We now match on the deterministic camp_section UUID + day-membership:
      //   1. enrollment must belong to this camp_section (location + week)
      //   2. its `selected_days` must include `dayNumber` (any session)
      //      OR `session_type` is an explicit full-week / full-camp signal.
      const res = await api.get('/enrollments/', { params: { page_size: 5000 } });
      const all = res.data?.results || res.data || [];
      const filtered = all
        .filter(e => e.status !== 'cancelled')
        .filter(e => String(e.camp_section || '') === String(sectionId))
        .map(e => {
          const days = Array.isArray(e.selected_days) ? e.selected_days : [];
          const isFullCamp = ['full_week', 'full_camp'].includes(e.session_type || '');
          const dayRow = days.find(d =>
            (d.day ?? d.day_number ?? d) === dayNumber && typeof d === 'object',
          );
          const onThisDay = isFullCamp || Boolean(dayRow) || days.includes(dayNumber);
          if (!onThisDay) return null;
          const session = isFullCamp
            ? 'full_day'
            : (dayRow?.session || 'full_day');
          return { ...e, _dayLabel: session };
        })
        .filter(Boolean);
      setRosterDialog(prev => ({ ...prev, enrollments: filtered, loading: false }));
    } catch {
      setRosterDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Group camps by week number
  const weekGroups = {};
  camps.forEach(camp => {
    const weekMatch = camp.name.match(/Week (\d+)/);
    const weekNum = weekMatch ? parseInt(weekMatch[1]) : 0;
    if (!weekGroups[weekNum]) weekGroups[weekNum] = { weekNum, camps: [] };
    weekGroups[weekNum].camps.push(camp);
  });

  // Build + download a PDF of the currently-open roster. Uses jsPDF +
  // autoTable so the doc is laid out as a real table (legible on paper,
  // sortable visually, scannable at a glance). Lives client-side so admins
  // get an instant download without hitting the API for a server-rendered
  // PDF blob.
  const downloadRosterPdf = async () => {
    if (!rosterDialog.enrollments.length) return;
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });

    const title = `Roster — ${rosterDialog.course}`;
    const wkBit = rosterDialog.weekNum != null ? `Week ${rosterDialog.weekNum}` : '';
    const dateBit = rosterDialog.sessionDate ? formatSessionDate(rosterDialog.sessionDate) : '';
    const dayBit = rosterDialog.dayNumber ? `Day ${rosterDialog.dayNumber}` : '';
    const subtitle = [wkBit, dateBit, rosterDialog.location, dayBit].filter(Boolean).join(' · ');
    const generated = `Generated ${new Date().toLocaleString()}`;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 40, 40);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, 40, 60);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(generated, 40, 75);
    doc.setTextColor(0);

    const sessionFor = (d) => ({ am: 'AM', pm: 'PM', full_day: 'Full Day' })[d] || 'Full Day';
    const rows = rosterDialog.enrollments.map((e, i) => [
      String(i + 1),
      e.learner_name || '—',
      e.learner_grade || '—',
      sessionFor(e._dayLabel),
      e.learner_allergies || '',
      e.learner_medical_notes || '',
      e.learner_photo_consent ? 'Yes' : 'No',
      e.extended_care_label || '',
      e.parent_name || '—',
      [e.parent_email, e.parent_phone].filter(Boolean).join('\n') || '—',
      [e.emergency_contact_name, e.emergency_contact_phone].filter(Boolean).join('\n') || '—',
    ]);

    autoTable(doc, {
      startY: 90,
      head: [[
        '#', 'Student', 'Grade', 'Session',
        'Allergies', 'Medical Notes', 'Photo OK', 'Ext. Care',
        'Parent', 'Parent Contact', 'Emergency Contact',
      ]],
      body: rows,
      styles: { fontSize: 8, cellPadding: 4, valign: 'top' },
      headStyles: { fillColor: [29, 78, 216], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 90 },
        2: { cellWidth: 36 },
        3: { cellWidth: 50 },
        4: { cellWidth: 90 },
        5: { cellWidth: 90 },
        6: { cellWidth: 46, halign: 'center' },
        7: { cellWidth: 60 },
        8: { cellWidth: 70 },
        9: { cellWidth: 110 },
        10: { cellWidth: 110 },
      },
      didParseCell: (data) => {
        // Highlight rows where there's any safety-critical info.
        if (data.section === 'body' && (data.column.index === 4 || data.column.index === 5) && data.cell.raw) {
          data.cell.styles.fillColor = data.column.index === 4 ? [254, 226, 226] : [255, 237, 213];
          data.cell.styles.textColor = [127, 29, 29];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.section === 'body' && data.column.index === 6 && data.cell.raw === 'No') {
          data.cell.styles.fillColor = [254, 226, 226];
          data.cell.styles.textColor = [127, 29, 29];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    const safeName = [
      rosterDialog.weekNum != null ? `Week${rosterDialog.weekNum}` : '',
      rosterDialog.sessionDate || '',
      rosterDialog.course,
      rosterDialog.location,
      rosterDialog.dayNumber ? `Day${rosterDialog.dayNumber}` : '',
    ].filter(Boolean).join('-').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
    doc.save(`Roster_${safeName}.pdf`);
    toast.success(`Downloaded roster (${rosterDialog.enrollments.length} student${rosterDialog.enrollments.length === 1 ? '' : 's'})`);
  };


  const sortedWeeks = Object.values(weekGroups).sort((a, b) => a.weekNum - b.weekNum);

  // Bug 2/3/4 helpers — pull per-section / per-cell stats out of campStats safely.
  const getSectionStats = (campId, secId) => {
    const stats = campStats[campId];
    if (!stats) return null;
    return (stats.sections || []).find(s => s.section_id === secId) || null;
  };
  const getCellCount = (campId, secId, dayNum, sessionKey) => {
    // sessionKey: 'am' | 'pm' | 'full_day'. We render per CampDay row which has
    // a session_type on each day. If the matrix row is full_day, total seats = full_day + AM + PM.
    const sec = getSectionStats(campId, secId);
    if (!sec) return null;
    const cell = sec.cells?.[String(dayNum)];
    if (!cell) return 0;
    if (sessionKey === 'full_day') {
      return (cell.full_day || 0) + (cell.am || 0) + (cell.pm || 0);
    }
    return (cell[sessionKey] || 0) + (cell.full_day || 0);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-4" data-testid="admin-camps-schedule">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold">{camps.length}</p>
          <p className="text-sm text-muted-foreground">Total Camps</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold">{sortedWeeks.length}</p>
          <p className="text-sm text-muted-foreground">Weeks</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold">{camps.reduce((sum, c) => sum + (c.sections?.length || 0), 0)}</p>
          <p className="text-sm text-muted-foreground">Location Sessions</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{camps.reduce((sum, c) => sum + (c.sections || []).reduce((s, sec) => s + (sec.enrolled_count || 0), 0), 0)}</p>
          <p className="text-sm text-muted-foreground">Total Enrolled</p>
        </CardContent></Card>
      </div>

      {/* Weeks */}
      {sortedWeeks.map(({ weekNum, camps: weekCamps }) => {
        const firstCamp = weekCamps[0];
        const firstSection = firstCamp?.sections?.[0];
        const dateRange = firstSection ? `${firstSection.start_date} – ${firstSection.end_date}` : '';
        const isExpanded = weekCamps.some(c => expandedWeeks[c.id]);

        return (
          <Card key={weekNum} className="overflow-hidden">
            {/* Week Header */}
            <div
              className="flex items-center justify-between p-4 bg-gradient-to-r from-rsc-navy/5 to-transparent cursor-pointer hover:bg-rsc-navy/10 transition-colors"
              onClick={() => weekCamps.forEach(c => setExpandedWeeks(prev => ({ ...prev, [c.id]: !isExpanded })))}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-rsc-navy flex items-center justify-center">
                  <span className="text-sm font-extrabold text-white">W{weekNum}</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg">Week {weekNum}</h3>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5" /> {dateRange}
                    <span className="mx-1">·</span>
                    {weekCamps.map(c => {
                      const grades = c.grade_bands?.join(', ') || '';
                      return grades;
                    }).filter(Boolean).join(' | ')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Bug 4 — RSS_Emergent_Fixes_May_11:
                    Replace the misleading aggregate "total enrolled" pill with
                    AVG (average registrants across this camp's sessions) and
                    FCR (Full-Camp Registrants — parents enrolled for every day).
                    Side-by-side, with a tooltip on each abbreviation. */}
                {weekCamps.map(c => {
                  const stats = campStats[c.id];
                  const secs = stats?.sections || [];
                  // Aggregate across all locations within this camp/grade-band.
                  const totalAvg = secs.length
                    ? (secs.reduce((s, sec) => s + (sec.avg_per_session || 0), 0) / secs.length).toFixed(1)
                    : '0.0';
                  const totalFcr = secs.reduce((s, sec) => s + (sec.full_camp_registrants || 0), 0);
                  return (
                    <div key={c.id} className="flex items-center gap-3" data-testid={`camp-header-stats-${c.id}`}>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <p className="text-base font-bold text-emerald-600 leading-none" data-testid={`avg-${c.id}`}>{totalAvg}</p>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help"><HelpCircle className="h-3 w-3 text-muted-foreground" /></span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[220px] text-xs">
                              Avg registrants across this location's sessions
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">AVG</p>
                      </div>
                      <span className="text-muted-foreground/40">·</span>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <p className="text-base font-bold text-rsc-navy leading-none" data-testid={`fcr-${c.id}`}>{totalFcr}</p>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help"><HelpCircle className="h-3 w-3 text-muted-foreground" /></span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[220px] text-xs">
                              Parents enrolled for the entire camp (all sessions)
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">FCR</p>
                      </div>
                      <span className="text-[10px] text-muted-foreground ml-1">{c.grade_bands?.join(', ')}</span>
                    </div>
                  );
                })}
                {isExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
              </div>
            </div>

            {/* Expanded: Show each grade band's courses and locations */}
            {isExpanded && (
              <CardContent className="p-0 border-t">
                {weekCamps.map(camp => (
                  <div key={camp.id} className="border-b last:border-b-0">
                    {/* Grade band header */}
                    <div className="px-4 py-2 bg-gray-50 flex items-center gap-2 flex-wrap">
                      <Badge className="bg-rsc-navy text-white">{camp.grade_bands?.join(', ') || 'All'}</Badge>
                      <span className="text-sm text-muted-foreground">{camp.name}</span>
                      <span className="ml-auto text-sm">
                        {(camp.sections || []).map(sec => {
                          // Bug (Feb 2026): the location header X/Y used to
                          // fall back to the stale CampSection.enrolled_count
                          // column (`sec.enrolled_count`), which routinely
                          // showed 0/20 in production even when the cells
                          // below showed enrolled students. Now we trust the
                          // stats endpoint exclusively — it computes the
                          // count from live confirmed enrollments. The
                          // optional column fallback is kept only for the
                          // ~50ms window before stats finish loading.
                          const secStats = getSectionStats(camp.id, sec.id);
                          const enrolled = secStats
                            ? secStats.enrolled_count ?? 0
                            : (sec.enrolled_count ?? 0);
                          const indet = secStats?.indeterminate_count || 0;
                          return (
                            <span key={sec.id} className="inline-flex items-center gap-1 mr-3">
                              <MapPin className="h-3 w-3" />
                              <span className="text-muted-foreground">{sec.location_name?.replace('Ready Set STEM - ', '')}</span>
                              <span className={`font-bold ${enrolled > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`} data-testid={`section-headcount-${sec.id}`}>
                                {enrolled}/{sec.capacity}
                              </span>
                              {indet > 0 && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        variant="outline"
                                        className="ml-1 px-1.5 py-0 text-[10px] cursor-help bg-amber-50 text-amber-700 border-amber-200"
                                        data-testid={`section-indeterminate-${sec.id}`}
                                      >
                                        +{indet}?
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <div className="text-xs max-w-xs">
                                        <p className="font-semibold mb-1">
                                          {indet} enrolment{indet === 1 ? '' : 's'} without day-by-day data
                                        </p>
                                        <p>
                                          These registrants are real but their
                                          per-day breakdown wasn&rsquo;t saved
                                          (legacy data). They&rsquo;re counted
                                          in the section total but not in any
                                          specific Mon/Tue/Wed cell below. Run{' '}
                                          <code className="bg-gray-100 px-1">
                                            backfill_camp_session_detail
                                          </code>{' '}
                                          to populate.
                                        </p>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </span>
                          );
                        })}
                      </span>
                    </div>

                    {/* Course schedule table */}
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="w-12">Day</TableHead>
                          <TableHead>Course</TableHead>
                          <TableHead className="w-24">Category</TableHead>
                          {(camp.sections || []).map(sec => (
                            <TableHead key={sec.id} className="text-center w-28">
                              <div className="flex flex-col items-center">
                                <span className="text-[10px]">{sec.location_name?.replace('Ready Set STEM - ', '').replace(' Junior High School', ' JH')}</span>
                              </div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(camp.days || []).map(day => (
                          <TableRow key={day.id} className="hover:bg-muted/30">
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {DAY_NAMES[day.day_number - 1]?.substring(0, 3) || `D${day.day_number}`}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium text-sm">{day.course_title}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs capitalize">{day.course_category || '—'}</Badge>
                            </TableCell>
                            {(camp.sections || []).map(sec => {
                              // Bug 3 — RSS_Emergent_Fixes_May_11:
                              // Render X/Y per cell where X = registrants for THIS
                              // (day, session_type) at THIS location, Y = section capacity.
                              // Keep the roster eyeball icon as a discoverable secondary action.
                              const sessionKey = (day.session_type || 'full_day').toLowerCase();
                              const cellCount = getCellCount(camp.id, sec.id, day.day_number, sessionKey);
                              const cap = sec.capacity || 0;
                              const hasEnrolled = cellCount > 0;
                              return (
                                <TableCell key={sec.id} className="text-center">
                                  <button
                                    onClick={() => fetchRoster({
                                      courseTitle: day.course_title,
                                      locationName: sec.location_name,
                                      sectionId: sec.id,
                                      dayNumber: day.day_number,
                                      weekNum,
                                      sectionStartDate: sec.start_date,
                                    })}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-rsc-navy/5 transition-colors cursor-pointer group"
                                    data-testid={`roster-${day.day_number}-${sec.id}`}
                                    title="View roster"
                                  >
                                    <span className={`text-sm font-bold tabular-nums ${hasEnrolled ? 'text-emerald-600' : 'text-muted-foreground/60'}`} data-testid={`cell-count-${day.day_number}-${sec.id}`}>
                                      {cellCount}/{cap}
                                    </span>
                                    <Eye className="h-3 w-3 text-muted-foreground/40 group-hover:text-rsc-navy transition-colors" />
                                  </button>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Roster Dialog */}
      <Dialog open={rosterDialog.open} onOpenChange={(open) => !open && setRosterDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <DialogTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" /> Enrolled Students — {rosterDialog.course}
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1" data-testid="roster-modal-subtitle">
                  {rosterDialog.weekNum != null && (
                    <span className="font-semibold text-rsc-navy">
                      Week {rosterDialog.weekNum}
                    </span>
                  )}
                  {rosterDialog.weekNum != null && rosterDialog.sessionDate && <span className="mx-1.5">·</span>}
                  {rosterDialog.sessionDate && (
                    <span className="font-medium text-rsc-navy" data-testid="roster-session-date">
                      {formatSessionDate(rosterDialog.sessionDate)}
                    </span>
                  )}
                  {(rosterDialog.weekNum != null || rosterDialog.sessionDate) && (
                    <span className="mx-1.5">·</span>
                  )}
                  {rosterDialog.location}
                  {rosterDialog.dayNumber ? (
                    <span className="ml-1.5 text-muted-foreground">
                      (Day {rosterDialog.dayNumber})
                    </span>
                  ) : null}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadRosterPdf}
                disabled={rosterDialog.loading || rosterDialog.enrollments.length === 0}
                className="flex-shrink-0 gap-1.5"
                data-testid="roster-download-pdf"
                title="Download roster as PDF"
              >
                <Download className="h-3.5 w-3.5" />
                Download PDF
              </Button>
            </div>
          </DialogHeader>
          {rosterDialog.loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : rosterDialog.enrollments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="roster-empty-state">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="font-medium">No students enrolled yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Session</TableHead>
                  <TableHead>Safety & Notes</TableHead>
                  <TableHead>Photo</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rosterDialog.enrollments.map(e => {
                  // Per-day session label — tells the admin whether the
                  // student is here AM-only, PM-only, or all day.
                  const sessionLabel = ({
                    am: 'AM',
                    pm: 'PM',
                    full_day: 'Full Day',
                  })[e._dayLabel] || 'Full Day';
                  const sessionVariant = ({
                    am: 'bg-sky-100 text-sky-700 border-sky-200',
                    pm: 'bg-amber-100 text-amber-700 border-amber-200',
                    full_day: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                  })[e._dayLabel] || 'bg-emerald-100 text-emerald-700 border-emerald-200';
                  const hasAllergies = Boolean(e.learner_allergies && e.learner_allergies.trim());
                  const hasMedical = Boolean(e.learner_medical_notes && e.learner_medical_notes.trim());
                  const hasExtCare = Boolean(e.extended_care_label);
                  const photoOk = Boolean(e.learner_photo_consent);
                  return (
                    <TableRow key={e.id} data-testid={`roster-row-${e.id}`}>
                      <TableCell className="font-medium">{e.learner_name}</TableCell>
                      <TableCell>{e.learner_grade || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${sessionVariant}`} data-testid={`roster-session-${e.id}`}>
                          {sessionLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        {!hasAllergies && !hasMedical && !hasExtCare && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                        {hasAllergies && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className="flex items-start gap-1 text-xs text-red-700 mb-1 cursor-help"
                                data-testid={`roster-allergies-${e.id}`}
                              >
                                <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                <span className="truncate font-medium">
                                  Allergies: {e.learner_allergies}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs whitespace-pre-wrap">
                              {e.learner_allergies}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {hasMedical && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className="flex items-start gap-1 text-xs text-orange-700 mb-1 cursor-help"
                                data-testid={`roster-medical-${e.id}`}
                              >
                                <Stethoscope className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                <span className="truncate">{e.learner_medical_notes}</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs whitespace-pre-wrap">
                              {e.learner_medical_notes}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {hasExtCare && (
                          <div
                            className="flex items-center gap-1 text-xs text-indigo-700"
                            data-testid={`roster-extcare-${e.id}`}
                          >
                            <Sunrise className="h-3 w-3 flex-shrink-0" />
                            <span>{e.extended_care_label}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {photoOk ? (
                              <Badge
                                variant="outline"
                                className="text-xs gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 cursor-help"
                                data-testid={`roster-photo-ok-${e.id}`}
                              >
                                <Camera className="h-3 w-3" /> OK
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-xs gap-1 bg-red-50 text-red-700 border-red-200 cursor-help"
                                data-testid={`roster-photo-no-${e.id}`}
                              >
                                <CameraOff className="h-3 w-3" /> No
                              </Badge>
                            )}
                          </TooltipTrigger>
                          <TooltipContent>
                            {photoOk
                              ? 'Parent has consented to photos/media'
                              : 'No photo/media consent — do NOT photograph'}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{e.parent_name || '—'}</TableCell>
                      <TableCell>
                        {e.parent_email && <div className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" /> {e.parent_email}</div>}
                        {e.parent_phone && (
                          <div
                            className="flex items-center gap-1 text-xs text-muted-foreground"
                            data-testid={`roster-parent-phone-${e.id}`}
                          >
                            <Phone className="h-3 w-3" /> {e.parent_phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell><Badge variant={e.status === 'confirmed' ? 'default' : 'secondary'}>{e.status}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
